'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@nextui-org/react'
import { Check } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from '@/constants/configs'

import { verifySchema, type VerifyFormValues } from '../hooks/redeem.schema'
import { useRedeemT } from '../i18n/useRedeemT'
import type {
  IGiftDetail,
  IRedeemDeeplink,
  IRedeemErrorVariant,
  IRedeemQueryPrefill,
  IUsedGiftBooking
} from '../types/schema'
import CaptchaModal from './CaptchaModal'
import ErrorState from './ErrorState'
import { CTA_PRIMARY, CTA_SECONDARY } from './styles'

interface Props {
  blocked: boolean
  locked: boolean
  isVerifying: boolean
  error: IRedeemErrorVariant | null
  usedBooking: IUsedGiftBooking | null
  /** The gift behind `locked`, once verified. Its values replace whatever was typed. */
  verifiedGift: IGiftDetail | null
  initialDeeplink: IRedeemDeeplink | null
  initialPrefill: IRedeemQueryPrefill | null
  onVerify: (values: VerifyFormValues) => Promise<boolean>
  onVerified: () => void
}

/**
 * Hero + inline verify card. Gate order matches the legacy page: the button submits the form, and
 * the captcha stands between a valid form and the network call.
 */
export default function VerifySection({
  blocked,
  locked,
  isVerifying,
  error,
  usedBooking,
  verifiedGift,
  initialDeeplink,
  initialPrefill,
  onVerify,
  onVerified
}: Props) {
  const t = useRedeemT()
  const [captchaOpen, setCaptchaOpen] = useState(false)
  const [pending, setPending] = useState<VerifyFormValues | null>(null)

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors }
  } = useForm<VerifyFormValues>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      giftFrom: initialDeeplink?.giftFrom ?? '',
      giftNumber: initialDeeplink?.giftNumber ?? ''
    }
  })


  useEffect(() => {
    if (!verifiedGift) return

    reset({ giftFrom: verifiedGift.receive_from, giftNumber: verifiedGift.gift_number })
  }, [verifiedGift, reset])

  const onSubmit = useCallback((values: VerifyFormValues) => {
    setPending(values)
    setCaptchaOpen(true)
  }, [])

  const onSolved = useCallback(async () => {
    setCaptchaOpen(false)
    if (pending && (await onVerify(pending))) onVerified()
    setPending(null)
  }, [pending, onVerify, onVerified])

  const gaayaUrl = env.GAAYA_REDEEM_URL?.trim()

  return (
    <section>
      {/*
        The dark band is required, not decoration: `.ui--navbar` is fixed, transparent and
        `text-white`, so a light hero makes the desktop nav invisible. `pt-28` clears its ~80px.
      */}
      <div className='bg-theme pb-24 pt-28 md:pt-32'>
        <div className='container mx-auto max-w-4xl px-4'>
          {/* Variant A eyebrow: hairline rule + spaced caps, in place of a filled chip. */}
          <div className='mb-5 flex items-center gap-3'>
            <span className='h-px w-8 bg-theme-100/50' aria-hidden />
            <span className='text-[11px] font-semibold uppercase tracking-[0.3em] text-theme-100'>
              {t('TITLE_REDEEM_GIFT')}
            </span>
          </div>

          <h1 className='text-3xl font-bold leading-tight text-white md:text-5xl'>
            {t('TITLE_SEARCH_GIFT')}
            <br />
            <span className='text-theme-orange'>{t('TITLE_HERO_ACCENT')}</span>
          </h1>
          {/* `text-theme-100` is the theme's cream for copy on `bg-theme` — WCAG AAA. */}
          <p className='mt-3 max-w-xl text-theme-100'>{t('DESCRIPTION_HERO')}</p>
        </div>
      </div>

      {/* Lifted over the band edge, so the band reads as a header not a separate slab. */}
      <div className='container mx-auto -mt-16 max-w-4xl px-4'>
        <div className='rounded-xl border border-gray-100 bg-white p-6 shadow-lg md:p-8'>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_1fr_auto]'>
              <Input
                {...register('giftFrom')}
                label={t('LABEL_INPUT_GIFT_FROM')}
                placeholder={t('LABEL_INPUT_GIFT_FROM_PLACEHOLDER')}
                variant='bordered'
                size='lg'
                isDisabled={locked}
                isInvalid={Boolean(errors.giftFrom)}
                errorMessage={errors.giftFrom && t('ERROR_INPUT_REQUIRED')}
              />
              <Input
                {...register('giftNumber')}
                label={t('LABEL_INPUT_GIFT_NUMBER')}
                placeholder={t('LABEL_INPUT_GIFT_NUMBER_PLACEHOLDER')}
                variant='bordered'
                size='lg'
                isDisabled={locked}
                isInvalid={Boolean(errors.giftNumber)}
                errorMessage={errors.giftNumber && t('ERROR_INPUT_REQUIRED')}
              />
              <button
                type='submit'
                disabled={blocked || locked || isVerifying}
                className={`${CTA_PRIMARY} h-14 min-w-40 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:scale-100 disabled:hover:bg-gray-300 disabled:hover:shadow-none`}>
                {locked ? (
                  <>
                    <Check className='h-4 w-4' /> {t('BUTTON_VERIFIED')}
                  </>
                ) : (
                  t('BUTTON_SEARCH')
                )}
              </button>
            </div>
          </form>

          {/* Cross-brand redeem. Hidden rather than dead when the env var is unset. */}
          {gaayaUrl && !locked && (
            <div className='mt-4 flex flex-col items-start justify-between gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center'>
              <span className='text-xs text-gray-500'>{t('DESCRIPTION_REDEEM_GAYA')}</span>
              <a
                href={gaayaUrl}
                target='_blank'
                rel='noopener noreferrer'
                className={`${CTA_SECONDARY} px-4 py-2`}>
                {t('BUTTON_REDEEM_GAYA')}
              </a>
            </div>
          )}

          {error && (
            <ErrorState
              variant={error}
              usedBooking={usedBooking}
              giftNumber={getValues('giftNumber')}
              prefill={initialPrefill}
            />
          )}
        </div>
      </div>

      <CaptchaModal
        isOpen={captchaOpen}
        onClose={() => {
          setCaptchaOpen(false)
          setPending(null)
        }}
        onSolved={onSolved}
      />
    </section>
  )
}
